import React, { FC, PropsWithChildren, useEffect, useState } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { ActionButton, ActionGroup, Icon, Tooltip } from 'ui'
import styles from '@styles/components/AddObjectRail.module.scss'
import {
  ToolActivationMode,
  ToolUI,
  useToolEvents,
  useTools
} from '@services/engine/useTools'
import CursorOverlay from '@components/cursorOverlay/CursorOverlay'
import {
  useMediumMinimumSizePreference,
  useProjectState
} from '@hooks/useProject'
import { useTranslations } from 'use-intl'
import { useActiveLayer } from '@hooks/useActiveLayer'
import RectangleIcon from '/public/icon_rectangle.svg'
import { useUISettings } from '@services/engine/useUISettings'
import { DocumentEventObserver } from '@services/engine/documentEventObserver'
import { Engine2DPrimitive } from '@services/engine/types'
import { CANVAS_ID } from '@services/engine/engine'
import { useSceneActions } from '@hooks/useScene'

export function AddObjectRail() {
  const [selected2DTool, setSelected2DTool] = useState<Tool | null>(null)
  const { add2DPrimitive } = useSceneActions()
  const showAddObjectRail = useProjectState('showAddObjectRail')
  const { is3DActiveLayer } = useActiveLayer()
  const t = useTranslations()
  const { resetPermanentTool } = useTools()
  const flags = useFlags()

  const threeDToolsToShow = flags['base-pf-ui-library-assets']
    ? [
        'addPrimitive_Box',
        'addPrimitive_Cylinder',
        'addPrimitive_Sphere',
        'addPrimitive_Cone',
        'addPrimitive_Poly',
        'addPrimitive_Egg',
        'addPrimitive_Torus',
        'addPrimitive_Horseshoe'
      ]
    : [
        'addPrimitive_Box',
        'addPrimitive_Cylinder',
        'addPrimitive_Sphere',
        'addPrimitive_Cone',
        'addPrimitive_Poly',
        'addPrimitive_Egg',
        'addPrimitive_Torus',
        'addPrimitive_Horseshoe',
        'addPrimitive_Capsule',
        'addPrimitive_HollowCone',
        'addPrimitive_Drop',
        'addPrimitive_Triangle',
        'addPrimitive_Star',
        'addPrimitive_Pin'
      ]

  const threeDToolUIs = useTools().toolUIs

  const twoDTools: Tool[] = [
    {
      toolType: Engine2DPrimitive.RECTANGLE,
      icon: <RectangleIcon />,
      tooltip: t('studio:toolbar:rectangle')
    }
  ]

  useEffect(() => {
    const canvas = document.getElementById(CANVAS_ID)
    if (!canvas) return

    const handleMouseClick = (e: MouseEvent) => {
      if (e.button !== 0 || !selected2DTool) return
      add2DPrimitive(selected2DTool.toolType)
      setSelected2DTool(null)
    }

    canvas.addEventListener('click', handleMouseClick)

    return () => canvas.removeEventListener('click', handleMouseClick)
  }, [selected2DTool])

  /*
  If both a 2d tool and a 3d tool are selected at the same time and the user attempts to add a primitive, an exception will be thrown. The selection of both types of tools simultaneously is currently possible because the 2d and 3d tools use different APIs. So we must manually deselect the 2d tool when the 3d tool is selected and vice versa.

  I expect this to change once both sets of tools use the same API. (JGAFAR)
  */
  useEffect(() => {
    if (is3DActiveLayer) {
      setSelected2DTool(null)
    } else {
      resetPermanentTool()
    }
  }, [is3DActiveLayer])

  if (!showAddObjectRail) return null

  return (
    <AddObjectRailContainer>
      {is3DActiveLayer ? (
        <ThreeDToolObjectRail
          tools={threeDToolUIs()}
          threeDToolsToShow={threeDToolsToShow}
        />
      ) : (
        <TwoDToolObjectRail
          tools={twoDTools}
          selectedTool={selected2DTool}
          setSelectedTool={setSelected2DTool}
        />
      )}
    </AddObjectRailContainer>
  )
}

function AddObjectRailContainer({ children }: PropsWithChildren) {
  return (
    <div className={styles['rail']}>
      <CursorOverlay />
      {children}
    </div>
  )
}

type ThreeDToolObjectRailProps = {
  tools: Array<ToolUI | undefined>
  threeDToolsToShow: string[]
}

function ThreeDToolObjectRail({
  tools,
  threeDToolsToShow
}: ThreeDToolObjectRailProps) {
  const { permanentToolID } = useTools()
  const t = useTranslations()
  const size = useMediumMinimumSizePreference()
  const { handleOnToolClick, handleOnToolDragStart } = useToolEvents()

  const { setMouseCaptureObject } = useUISettings()

  setMouseCaptureObject({
    enableMouseCapture: (enable: boolean): void => {
      DocumentEventObserver.instance()?.enableMouseCapture(enable)
    }
  })

  return (
    <ActionGroup
      selects="single"
      selected={[permanentToolID()]}
      className={styles['action-group']}
    >
      {tools
        ?.filter((tool: ToolUI | undefined) => {
          const id = tool?.id()
          return threeDToolsToShow.some(pId => pId === id)
        })
        .map(tool => {
          if (!tool) return null
          const id = tool?.id()
          return (
            <ActionButton
              className={styles['action-button']}
              data-icon-size={size}
              key={id}
              value={id}
              draggable
              onDragStart={() => handleOnToolDragStart(tool)}
              onClick={e => handleOnToolClick(tool)}
            >
              {tool.icon()}
              <Tooltip selfManaged placement="right" offset={2}>
                {tool.tooltipLabel(t)}
              </Tooltip>
            </ActionButton>
          )
        })}
    </ActionGroup>
  )
}

type Tool = {
  toolType: Engine2DPrimitive
  icon: React.ReactNode
  tooltip: string
}

type TwoDToolObjectRailProps = {
  tools: Tool[]
  selectedTool: Tool | null
  setSelectedTool: (tool: Tool | null) => void
}

function TwoDToolObjectRail({
  tools,
  selectedTool,
  setSelectedTool
}: TwoDToolObjectRailProps) {
  return (
    <div>
      {tools.map(tool => (
        <ToolButton
          key={tool.toolType}
          tool={tool}
          setSelectedTool={setSelectedTool}
          selectedTool={selectedTool}
        />
      ))}
    </div>
  )
}

type ToolButtonProps = {
  tool: Tool
  setSelectedTool: (tool: Tool | null) => void
  selectedTool: Tool | null
}

function ToolButton({ tool, setSelectedTool, selectedTool }: ToolButtonProps) {
  const size = useMediumMinimumSizePreference()

  const handleOnClick = () => {
    if (selectedTool) return setSelectedTool(null)
    setSelectedTool(tool)
  }

  return (
    <ActionButton
      className={styles['action-button']}
      data-icon-size={size}
      value={tool.toolType.toString()}
      draggable
      selected={selectedTool?.toolType === tool.toolType}
      onClick={handleOnClick}
    >
      <Icon slot="icon">{tool.icon}</Icon>
      <Tooltip selfManaged placement="right" offset={2}>
        {tool.tooltip}
      </Tooltip>
    </ActionButton>
  )
}
